import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { type Err } from '../CreateActivityTagModal/types'
import { isActivityTagNameInvalid, MAX_ACTIVITY_TAG_NAME_LENGTH } from '../activityTagName'

import { useEditActivityTag } from './useEditActivityTag'
import { ActionModal, FormField, Modal, TextInput } from '@percent/lemonade'

export type EditActivityTagModalProps = Readonly<{
  isOpen: boolean
  handleClose: () => void
  tag: { id: string; name: string } | null
  onSuccess: () => void
  onError: (error?: Err) => void
}>

export function EditActivityTagModal({ isOpen, handleClose, tag, onSuccess, onError }: EditActivityTagModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(tag?.name ?? '')

  useEffect(() => {
    if (isOpen && tag) {
      setName(tag.name)
    }
  }, [isOpen, tag])

  const { isLoading, editActivityTag } = useEditActivityTag({
    onEdited: () => {
      onSuccess()
    },
    onError: (error: Err) => {
      onError(error)
    }
  })

  const onClose = () => {
    if (tag) {
      setName(tag.name)
    }
    handleClose()
  }

  const trimmedName = name.trim()
  const originalName = tag?.name.trim() ?? ''
  const isNameInvalid = isActivityTagNameInvalid(trimmedName)
  const isSubmitDisabled = !trimmedName || isNameInvalid || trimmedName === originalName

  const fieldStatus = isNameInvalid ? 'danger' : 'default'
  const statusMessage = isNameInvalid ? t('errorMessage.activityTagNameInvalid') : undefined

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ActionModal
        title={t('typography.editActivityTagTitle')}
        primaryButtonText={t('button.save')}
        secondaryButtonText={t('button.cancel')}
        handleClose={onClose}
        handleSubmit={() => tag && editActivityTag(tag.id, trimmedName)}
        loading={isLoading}
        disabled={isSubmitDisabled}
        primaryBtnTestId="edit-activity-tag-save-button"
        secondaryBtnTestId="edit-activity-tag-cancel-button"
      >
        <FormField
          label={t('typography.activityTagNameLabel')}
          necessity="required"
          status={fieldStatus}
          statusMessage={statusMessage}
        >
          <TextInput
            name="activityTagName"
            value={name}
            placeholder={t('typography.activityTagNamePlaceholder')}
            onChange={e => setName(e.target.value)}
            maxLength={MAX_ACTIVITY_TAG_NAME_LENGTH}
            data-testid="edit-activity-tag-name-input"
          />
        </FormField>
      </ActionModal>
    </Modal>
  )
}
